import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2,
  ChevronUp, ChevronDown, Truck, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { DeliveryOption } from '../../types';
import DeliveryOptionFormModal from '../../components/admin/DeliveryOptionFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

const DeliveryOptionsManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DeliveryOption | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('display_order');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });


  useEffect(() => {
    loadDeliveryOptions();
  }, []);

  const loadDeliveryOptions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDeliveryOptions();
      
      let optionsList: DeliveryOption[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          optionsList = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          optionsList = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          optionsList = response.data.data;
        }
      } else if (Array.isArray(response)) {
        optionsList = response;
      }
      
      setDeliveryOptions(optionsList);
    } catch (error) {
      console.error('Failed to load delivery options:', error);
      setToast({ show: true, message: 'Failed to load delivery options', type: 'error' });
      setDeliveryOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (option: DeliveryOption) => {
    setSelectedOption(option);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (option: DeliveryOption) => {
    setSelectedOption(option);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOption) return;
    
    try {
      await adminAPI.deleteDeliveryOption(selectedOption.id);
      setToast({ show: true, message: 'Delivery option deleted successfully', type: 'success' });
      loadDeliveryOptions();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete delivery option', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedOption(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedOption) {
        await adminAPI.updateDeliveryOption(selectedOption.id, formData);
        setToast({ show: true, message: 'Delivery option updated successfully', type: 'success' });
      } else {
        await adminAPI.createDeliveryOption(formData);
        setToast({ show: true, message: 'Delivery option created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedOption(null);
      loadDeliveryOptions();
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedOption(null);
    setIsModalOpen(true);
  };


  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const filtered = useMemo(() => {
    let list = Array.isArray(deliveryOptions) ? deliveryOptions : [];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(o =>
        o.name_en?.toLowerCase().includes(q) ||
        o.name_bn?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [deliveryOptions, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Delivery Options</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Add Option <span className="adm-btn-badge">{deliveryOptions.length}</span>
          </button>
          <button className="adm-btn adm-btn-refresh" onClick={loadDeliveryOptions} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filtered.length}</strong> options</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by name…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading options…</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No delivery options found.</div>}
          {!loading && filtered.map((o: DeliveryOption) => (
            <div className="mobile-card" key={o.id}>
              <div className="mc-top">
                <div className="mc-thumb-placeholder">{o.icon || <Truck size={22} />}</div>
                <div className="mc-info">
                  <div className="mc-title">{o.name_en}</div>
                  <div className="mc-subtitle">{o.name_bn}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${o.is_active ? 'active' : 'inactive'}`}>{o.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <div className="mc-meta">
                <div className="mc-meta-item"><span className="mc-meta-label">Cost:</span><span className="mc-meta-value">৳{Number(o.cost).toFixed(2)}</span></div>
                <div className="mc-meta-item"><span className="mc-meta-label">Order:</span><span className="mc-meta-value">{o.display_order}</span></div>
              </div>
              <div className="mc-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(o)}><Pencil size={12} /> Edit</button>
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(o)}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th" style={{ width: 50 }}>Icon</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('name_en')}>Name EN <SA field="name_en" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('name_bn')}>Name BN <SA field="name_bn" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('cost')}>Cost <SA field="cost" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('display_order')}>Order <SA field="display_order" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                  <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading…</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={7} className="adm-empty">No delivery options found.</td></tr>}
                {!loading && paginated.map((o: DeliveryOption) => (
                  <tr className="adm-row" key={o.id}>
                    <td className="adm-td" style={{ fontSize: 22, textAlign: 'center' }}>{o.icon || <Truck size={18} />}</td>
                    <td className="adm-td"><span className="adm-name-main">{o.name_en}</span></td>
                    <td className="adm-td"><span className="adm-text-muted">{o.name_bn}</span></td>
                    <td className="adm-td"><span className="adm-price-main" style={{ fontSize: 12 }}>৳{Number(o.cost).toFixed(2)}</span></td>
                    <td className="adm-td"><span className="adm-text-bold">{o.display_order}</span></td>
                    <td className="adm-td">
                      <span className={`adm-chip ${o.is_active ? 'adm-chip-green' : 'adm-chip-red'}`}>
                        {o.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}
                      </span>
                    </td>
                    <td className="adm-td" style={{ padding: '6px 4px' }}>
                      <div className="adm-actions-row">
                        <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(o)} title="Edit"><Pencil size={12} /></button>
                        <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(o)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 options' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <DeliveryOptionFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOption(null);
          }}
          onSave={handleSave}
          deliveryOption={selectedOption}
        />
      )}

      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          title="Delete Delivery Option"
          message={`Are you sure you want to delete "${selectedOption?.name_en}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSelectedOption(null);
          }}
        />
      )}

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default DeliveryOptionsManagement;
